import { validationHelper } from 'libs/helper/validationHelper';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, InputField, Row, useModelStateContext } from 'saladsoft-primitive';
import { EmailSignUpDto, UserProfileDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import styled from 'styled-components';
import { format } from 'react-string-format';
import PasswordField from 'components/PasswordField';

const SignupPage = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { stateModel, setState, setMultipleState } = useModelStateContext<EmailSignUpDto>();

  const [isValid, setIsValid] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    name: '',
  });

  const [confirmPassword, setConfirmPassword] = useState<string>();
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);

  const [showErrors, setShowErrors] = useState({
    email: false,
    password: false,
    name: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateField = (field: 'email' | 'password' | 'name', value: string) => {
    if (!value) return field === 'name' ? t('nameRequired') : ''; // 이름은 빈값 검증 메시지 표시

    if (field === 'email') {
      if (!validationHelper.email(value)) {
        return t('emailHepler');
      }
    } else if (field === 'password') {
      if (!validationHelper.password(value)) {
        return t('passwordHelper');
      }
    }
    return '';
  };

  const handleBlur = (field: 'email' | 'password' | 'name') => {
    let value;
    if (field === 'email') {
      value = stateModel?.username;
    } else if (field === 'password') {
      value = stateModel?.password;
    } else {
      value = stateModel?.profile?.name;
    }

    if (!value) return;

    setShowErrors((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handleEmailSignup = async () => {
    if (stateModel) {
      const response = await ServiceHelper.authService?.signUpWithEmail(stateModel);
      if (response?.status === 200) {
        toast.success(t('signupSuccess'));
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast.error(t('signupFailed'));
      }
    }
  };

  useEffect(() => {
    const userName = validationHelper.email(stateModel?.username);
    const matchPassword = stateModel?.password === confirmPassword;
    const password = validationHelper.password(stateModel?.password);
    const name = validationHelper.required(stateModel?.profile?.name);
    setIsValid(userName && password && matchPassword && name);

    // 이메일, 패스워드, 이름 에러 상태 업데이트
    setErrors({
      email: validateField('email', stateModel?.username || ''),
      password: validateField('password', stateModel?.password || ''),
      name: validateField('name', stateModel?.profile?.name || ''),
    });
  }, [stateModel, confirmPassword]);

  useEffect(() => {
    const profile: UserProfileDto = {
      countryCode: '+82',
      phoneNumber: '010-4155-8477',
      birthday: '1995-01-01',
    };
    setMultipleState({ profile });
  }, []);

  return (
    <Container>
      <LoginBg
        style={{ flex: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
      >
        <div style={{ margin: 'auto' }}>
          <div style={{ fontSize: 20, fontWeight: 'bold', color: 'white' }}>KSE</div>
          <div>
            <img src="/images/customsai.svg" style={{ width: 200 }} />
          </div>

          <div style={{ fontSize: 12, fontWeight: '400', color: 'white', opacity: 0.5 }}>
            {/* {packageJson.version} */}
          </div>
          <div style={{ marginTop: 40, color: 'white', opacity: 0.5 }}>
            Leading logistics systems with
            <br /> AI technology.
          </div>
        </div>
      </LoginBg>
      <div
        style={{
          flex: 0.5,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          flexDirection: 'column',
        }}
      >
        <LoginText>Sign up</LoginText>
        <div
          style={{
            fontSize: 12,
            maxWidth: 500,
            width: '100%',
            marginBottom: 20,
            fontWeight: '400',
            textAlign: 'left',
            color: '#8c8c8c',
          }}
        >
          {t('confirmRequired')}
        </div>

        <Row flexDirection="column" gap="30px" width="100%" maxWidth={500}>
          <InputWrapper>
            <InputField
              label="email"
              onChange={(value: string) => setState('username', value)}
              onBlur={() => handleBlur('email')}
              helperText={showErrors.email && stateModel?.username ? errors.email : ''}
              error={!!errors.email && !!stateModel?.username}
            />
          </InputWrapper>
          <InputWrapper>
            <PasswordField
              label="password"
              value={stateModel?.password}
              onChange={(value: string) => setState('password', value)}
              onBlur={() => handleBlur('password')}
              helperText={showErrors.password && stateModel?.password ? errors.password : ''}
              error={!!errors.password && !!stateModel?.password}
            />
          </InputWrapper>
          <InputWrapper>
            <PasswordField
              label="confirm password"
              // type={showConfirmPassword ? 'text' : 'password'}
              onChange={(value: string) => {
                setConfirmPassword(value);
                setConfirmPasswordError(stateModel?.password !== value);
              }}
              helperText={confirmPasswordError ? t('passwordMismatch') : ''}
              error={confirmPasswordError}
            />
          </InputWrapper>
          <InputWrapper>
            <InputField
              label="name"
              onChange={(value: string) =>
                setState('profile', { ...stateModel?.profile, name: value })
              }
              onBlur={() => handleBlur('name')}
              helperText={showErrors.name && stateModel?.profile?.name ? errors.name : ''}
              error={!!errors.name && !!stateModel?.profile?.name}
            />
          </InputWrapper>
          <div style={{ marginTop: '10px', width: '100%' }}>
            <Button
              isValid={isValid}
              onClick={() => {
                handleEmailSignup();
              }}
            >
              {t('signup')}
            </Button>
          </div>
        </Row>
        <div
          style={{
            width: '100%',
            maxWidth: 500,
            marginTop: 20,
            fontSize: 12,
            fontWeight: '400',
            textAlign: 'left',
          }}
        >
          {format(t('loginPage'), <Link to="/login">{t('login')}</Link>)}
        </div>
      </div>
    </Container>
  );
};

const Container = styled.div`
  justify-content: space-between;
  display: flex;
  width: 100vw;
  height: 100vh;
  /* max-width: 1800px; */
  margin: auto;
  justify-content: center;
  align-items: center;
`;

const InputWrapper = styled.div`
  width: 100%;
  height: 80px;
`;

const LoginBg = styled.div`
  background-image: url('/images/loginbg.jpg');
  background-size: cover;
  background-position: center;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  animation: slideBackground 20s ease-out infinite alternate;

  @keyframes slideBackground {
    from {
      background-position: -240px center;
    }
    to {
      background-position: 0px center;
    }
  }
`;

const LoginText = styled.div`
  text-align: left;
  width: 500px;
  font-size: 30px;
  font-weight: 600;
  margin-bottom: 40px;
`;

export default SignupPage;
